import React, { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronLeftIcon,
  KeyIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'

function StatusPill({ keyRow }) {
  if (keyRow.hasDbKey) {
    const ok = keyRow.lastTestStatus === 'ok'
    const failed = keyRow.lastTestStatus === 'error'
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
        style={{
          background: ok ? '#E1F5EE' : failed ? '#FCEBEB' : '#EEF2FF',
          color: ok ? '#085041' : failed ? '#791F1F' : '#3730A3',
        }}
      >
        {ok && <CheckCircleIcon className="w-3.5 h-3.5" />}
        {failed && <ExclamationTriangleIcon className="w-3.5 h-3.5" />}
        {ok ? 'Tested OK' : failed ? 'Last test failed' : 'Stored — not tested'}
      </span>
    )
  }
  if (keyRow.hasEnvKey) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
        style={{ background: '#E6F1FB', color: '#0C447C' }}
      >
        Using env var
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: '#FAEEDA', color: '#633806' }}
    >
      Needs key
    </span>
  )
}

function KeyRow({ row, onSaved, onTested, onDeleted }) {
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [msg, setMsg] = useState(null)

  const save = async () => {
    if (!value.trim()) return
    setSaving(true)
    setMsg(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/patron-screening/keys/${encodeURIComponent(row.sourceId)}`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value: value.trim(), isEnabled: true }),
        }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      setValue('')
      setMsg({ kind: 'ok', text: 'Saved.' })
      onSaved?.()
    } catch (e) {
      setMsg({ kind: 'err', text: e.message || 'Save failed' })
    } finally {
      setSaving(false)
    }
  }

  const test = async () => {
    setTesting(true)
    setMsg(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/patron-screening/keys/${encodeURIComponent(row.sourceId)}/test`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) }
      )
      const json = await res.json().catch(() => ({}))
      if (json.success) {
        setMsg({ kind: 'ok', text: `Test: ${json.status} — ${json.summary || ''}` })
      } else {
        setMsg({ kind: 'err', text: json.error || json.summary || 'Test failed' })
      }
      onTested?.()
    } catch (e) {
      setMsg({ kind: 'err', text: e.message || 'Test failed' })
    } finally {
      setTesting(false)
    }
  }

  const del = async () => {
    if (!row.hasDbKey) return
    if (!confirm(`Remove stored key for ${row.sourceName}?`)) return
    setDeleting(true)
    setMsg(null)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/patron-screening/keys/${encodeURIComponent(row.sourceId)}`),
        { method: 'DELETE' }
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      setMsg({ kind: 'ok', text: 'Removed.' })
      onDeleted?.()
    } catch (e) {
      setMsg({ kind: 'err', text: e.message || 'Delete failed' })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="border-b border-gray-100 last:border-0 py-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-psg-teal">{row.sourceName}</span>
            <StatusPill keyRow={row} />
          </div>
          <div className="text-xs text-gray-500 mt-0.5 font-mono">
            source id: {row.sourceId}
            {row.envVar ? <> · env: {row.envVar}</> : null}
          </div>
          {row.hasDbKey && (
            <div className="text-xs text-gray-500 mt-0.5">
              Stored value: <span className="font-mono">{row.maskedValue || '•••••'}</span>
              {row.updatedBy ? <> · by {row.updatedBy}</> : null}
              {row.updatedAt ? <> · {new Date(row.updatedAt).toLocaleString()}</> : null}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <input
          type="password"
          autoComplete="new-password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={row.hasDbKey ? 'Paste a new key to replace…' : 'Paste API key…'}
          className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 outline-none focus:border-gray-300 font-mono"
        />
        <Button size="sm" onClick={save} disabled={!value.trim() || saving}>
          {saving ? 'Saving…' : row.hasDbKey ? 'Replace' : 'Save'}
        </Button>
        <Button size="sm" variant="outline" onClick={test} disabled={testing || (!row.hasDbKey && !row.hasEnvKey)}>
          {testing ? 'Testing…' : 'Test'}
        </Button>
        {row.hasDbKey && (
          <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={del} disabled={deleting}>
            <TrashIcon className="w-4 h-4 mr-1" />
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        )}
      </div>
      {msg && (
        <div
          className={`mt-2 text-xs ${msg.kind === 'ok' ? 'text-green-600' : 'text-red-600'}`}
        >
          {msg.text}
        </div>
      )}
      {row.lastTestStatus === 'error' && row.lastTestError && (
        <div className="mt-2 text-xs text-red-600">
          Last test error: {row.lastTestError}
        </div>
      )}
    </div>
  )
}

export default function ScreeningApiKeys() {
  const [keys, setKeys] = useState([])
  const [masterKeyConfigured, setMasterKeyConfigured] = useState(true)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/patron-screening/keys'))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) {
        throw new Error(json.error || `HTTP ${res.status}`)
      }
      setKeys(json.keys || [])
      setMasterKeyConfigured(!!json.masterKeyConfigured)
    } catch (e) {
      setErr(e.message || 'Failed to load keys')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  return (
    <div className="space-y-6">
      <Link to="/compliance-safe/ecdd" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
        <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD
      </Link>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <KeyIcon className="w-5 h-5 text-psg-teal" />
            <h2 className="text-xl font-bold text-psg-teal m-0">OSINT Screening — API Keys</h2>
          </div>
          <p className="text-sm text-gray-500 m-0">
            Store per-tenant API keys for OSINT patron-screening sources. Keys are encrypted at rest
            (AES-256-GCM) and only decrypted when the screening tool makes a live call.
          </p>
        </CardContent>
      </Card>

      {!masterKeyConfigured && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-800">
            <b>Encryption master key not configured.</b> Set{' '}
            <code className="font-mono bg-amber-100 px-1 rounded">SCREENING_KEYS_MASTER_KEY</code>{' '}
            in the backend environment (32 bytes hex or base64) before storing keys. Generate with:
            <pre className="text-xs mt-2 bg-amber-100/60 rounded p-2 overflow-x-auto">node -e "console.log(require('crypto').randomBytes(32).toString('hex'))"</pre>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5">
          {loading ? (
            <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
          ) : err ? (
            <div className="py-8 text-center text-red-600 text-sm">{err}</div>
          ) : keys.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              No key-based sources configured.
            </div>
          ) : (
            <div>
              {keys.map((row) => (
                <KeyRow
                  key={row.sourceId}
                  row={row}
                  onSaved={load}
                  onTested={load}
                  onDeleted={load}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
